import React, {
  useCallback, useEffect, useRef, useState,
} from "react";

import moment from "moment";

import CustomCalendar from "./CustomCalendar";
import WeekMonthCheckbox from "./WeekMonthCheckbox";

import "./WeekFrequencyForm.css";

export default function WeekFrequencyForm({
  allowMultiple = true,
  updateSamplingData,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  weekDays,
  setWeekDays,
  layout = "env-scheduling", // value can be "env-scheduling" or "supply-request-recurrence"
  minStartDate,
  allowPastStartDate = false,
  apiDates,
}) {
  const [recurrences, setRecurrences] = useState(0);
  const ref = React.useRef();
  const useApiDatesRef = useRef(Boolean(apiDates?.length));

  useEffect(() => {
    useApiDatesRef.current = Boolean(apiDates?.length);
  }, [apiDates]);

  const markUserChanged = () => {
    useApiDatesRef.current = false;
  };

  const flatPickerOptions = {
    mode: "single",
    minDate: minStartDate ?? moment().format("MM/DD/YYYY"),
    // maxDate: moment().add(1, "year").format("MM/DD/YYYY"),
  };

  /**
   * Find no. of recurrences
   */
  const getRecurrences = useCallback(() => {
    const start = moment(startDate, "MM/DD/YYYY");
    const end = moment(endDate, "MM/DD/YYYY");
    if (!end.isAfter(start, "day")) {
      return { recurrences: 0, dates: [] };
    }
    let count = 0;
    const dates = [];
    const currentDate = start.clone();
    while (currentDate.isSameOrBefore(end, "day")) {
      if (weekDays.includes(currentDate.format("dddd").toLowerCase())) {
        dates.push(currentDate.format("MM/DD/YYYY"));
        count++;
      }
      currentDate.add(1, "day");
    }
    return { recurrences: count, dates };
  }, [endDate, startDate, weekDays]);

  const supplyEndPickerMinDate = moment(startDate, "MM/DD/YYYY").add(1, "day").format("MM/DD/YYYY");
  const showRecurrenceError = recurrences === 0;

  useEffect(() => {
    if (useApiDatesRef.current && apiDates?.length > 0) {
      setRecurrences(apiDates.length);
      return;
    }
    const { recurrences: recurr, dates } = getRecurrences();
    setRecurrences(recurr);
    updateSamplingData(dates, "weekly");
  }, [getRecurrences, apiDates]); // eslint-disable-line

  /**
   * Handles change of week days
   * @param {string[]} value Array of selected days
   */
  const handleChange = (value) => {
    markUserChanged();
    setWeekDays(value);
  };

  if (layout === "supply-request-recurrence") {
    return (
      <div className="FrequencyForm__Content WeekFrequencyForm SupplyRequest">
        <div className="StartDateSelection">
          <div className="Field SupplyRequestField">
            <span className="FieldTitle">Start Date</span>
            <CustomCalendar
              ref={ref}
              value={startDate}
              allowPastDates={allowPastStartDate}
              onChange={([date]) => {
                markUserChanged();
                const momentDate = moment(date);
                const formattedDate = momentDate.format("MM/DD/YYYY");
                setStartDate(formattedDate);
                if (!endDate || !moment(endDate, "MM/DD/YYYY").isAfter(momentDate, "day")) {
                  setEndDate(momentDate.clone().add(1, "day").format("MM/DD/YYYY"));
                }
              }}
              options={{ mode: "single" }}
            />
          </div>
          <div className="Field SupplyRequestField">
            <span className="FieldTitle">End date</span>
            <CustomCalendar
              value={endDate}
              allowPastDates={allowPastStartDate}
              onChange={([date]) => {
                markUserChanged();
                const momentDate = moment(date);
                if (!momentDate.isAfter(moment(startDate, "MM/DD/YYYY"), "day")) return;
                setEndDate(momentDate.format("MM/DD/YYYY"));
              }}
              options={{
                mode: "single",
                minDate: supplyEndPickerMinDate,
              }}
            />
          </div>
        </div>
        <div className="Field SupplyRequestField BorderBottom">
          <span className="FieldTitle">Select day of repeat</span>
          <WeekMonthCheckbox
            allowMultiple={allowMultiple}
            type="day"
            onChange={handleChange}
            selectedDay={weekDays}
          />
        </div>
        <div className="Field SupplyRequestField">
          <span className="FieldTitle WeekFrequencyForm__RecurrenceText--margin-right">No of Recurrences</span>
          <div className="Recurrences">
            {recurrences}
          </div>
          {showRecurrenceError && (
            <span className="SupplyRequestRecurrenceForm__Error">
              At Least One recurrence is required for the selected date
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="WeekFrequencyForm">
      <div className="Field">
        <span className="FieldTitle">Start Date</span>
        <CustomCalendar
          ref={ref}
          value={startDate}
          onChange={([date]) => {
            const momentDate = moment(date);
            const formattedDate = momentDate.format("MM/DD/YYYY");
            setStartDate(formattedDate);
            if (endDate && moment(endDate, "MM/DD/YYYY").isBefore(momentDate, "day")) {
              setEndDate(formattedDate);
            }
          }}
          options={flatPickerOptions}
        />
      </div>
      <div className="Field">
        <span className="FieldTitle">Select day of repeat</span>
        <WeekMonthCheckbox
          allowMultiple={allowMultiple}
          type="day"
          onChange={handleChange}
          selectedDay={weekDays}
        />
      </div>
      <div className="Field">
        <span className="FieldTitle">End date</span>
        <CustomCalendar
          value={endDate}
          onChange={([date]) => {
            if (startDate && moment(startDate, "MM/DD/YYYY").isAfter(moment(date), "day")) return;
            setEndDate(date);
          }}
          options={{
            ...flatPickerOptions,
            minDate: startDate,
          }}
        />
        <span className="FieldTitle WeekFrequencyForm__RecurrenceText">No of Recurrences</span>
        <div className="Recurrences">
          {recurrences}
        </div>
      </div>
    </div>
  );
}
